import type { FC } from "react";
import { useEffect, useState } from "react";
import { Button, Modal, Text } from "@uhg-harmony/react";
import { createLogger } from "../../features/notifications/utils/logger";
import type { ModalProps } from "@uhg-harmony/react";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { templatesActions } from "../../redux/templates/templatesSlice";
import { selectTemplatesLoading } from "../../redux/templates/templatesTreeSelectors";
import { useAuth } from "react-oidc-context";
import { sendTemplateNotification } from "../../features/notifications/utils/sendTemplateNotification";
import type { PublishTemplateOperationResponse } from "../../redux/templates/templatesTypes";
import { ErrorContainer } from "./shared";
import { selectFolderName, selectCategory, selectAccountId } from "../../redux/metadata/metadataSlice";

const logger = createLogger("Publish");

type PublishModalProps = ModalProps & {
  open: boolean;
  onClose: () => void;
  onConfirm?: (result: PublishTemplateOperationResponse) => void;
  templateName?: string;
  draftInfo?: {
    templateRowId: string;
    platformId: number;
    clientId: number;
  } | null;
};

export const PublishModal: FC<PublishModalProps> = ({
  title,
  open,
  onClose,
  onConfirm,
  templateName,
  draftInfo,
}) => {
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const [isPublishing, setIsPublishing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Access Redux state for templates
  const templatesState = useAppSelector((state) => state.templates);
  const isLoading = useAppSelector(selectTemplatesLoading);
  const folderName = useAppSelector(selectFolderName);
  const category = useAppSelector(selectCategory);
  const accountId = useAppSelector(selectAccountId);
  const platformId = useAppSelector((state) => state.user.platformId);
  const lastOperation = useAppSelector(
    (state) => state.templates.lastOperation,
  );

  // Clear error when modal opens
  useEffect(() => {
    if (open) {
      setErrorMessage("");
      setIsPublishing(false);
    }
  }, [open]);

  // Monitor publish operation response
  useEffect(() => {
    if (
      !isPublishing ||
      !lastOperation ||
      lastOperation.name !== "publishTemplate"
    ) {
      return;
    }

    // Handle success case
    if (!isLoading && lastOperation.result && !templatesState.error) {
      // Publish notification via ECP notification system
      sendTemplateNotification(
        auth.user?.access_token,
   
        "Template Published Successfully",
        `Publish Template Name - ${templateName?.trim() || "Unnamed"} saved successfully at Folder: ${folderName}!`,
        category,
      
        (error) => logger.error("Failed to send publish notification:", error),
      );

      setIsPublishing(false);
      onConfirm?.(lastOperation.result as PublishTemplateOperationResponse);
      onClose();
    }

    // Handle failure case
    if (!isLoading && templatesState.error) {
      logger.error("Publish failed:", templatesState.error);
      setErrorMessage(`Failed to publish template: ${templatesState.error}`);
      setIsPublishing(false);
    }
  }, [
    isPublishing,
    isLoading,
    lastOperation,
    templatesState.error,
    templateName,
    folderName,
    category,
    accountId,
    auth.user?.access_token,
    auth.user?.profile?.sub,
    onConfirm,
    onClose,
  ]);

  const handleConfirm = () => {
    setIsPublishing(true);
    setErrorMessage("");

    // Check if we have draft info with template data
    if (draftInfo && draftInfo.templateRowId) {
      // Get auth token
      if (!auth.user?.access_token) {
        setErrorMessage("Authentication required");
        setIsPublishing(false);
        return;
      }

      // Prepare publish request data
      const publishRequestData = {
        token: auth.user.access_token,
        templateId: draftInfo.templateRowId,
        platformClientsId: draftInfo.clientId
          ? String(draftInfo.clientId)
          : "",
        platformId: draftInfo.platformId
          ? String(draftInfo.platformId)
          : "",
      };

      // Dispatch publish template action
      dispatch(templatesActions.publishTemplateRequested(publishRequestData));
    } else {
      setErrorMessage("No draft template information available");
      setIsPublishing(false);
    }
  };

  return (
    <>
      {open && (
        <Modal open={open} title={title} aria-label={typeof title === 'string' ? title : 'Publish Template'} onClose={onClose}>
          <Modal.Body>
            {errorMessage && (
              <ErrorContainer>
                <strong>Error:</strong> {errorMessage}
              </ErrorContainer>
            )}
            <Text size="xlarge">
              Any unsaved changes will be lost. Are you sure you want to publish{" "}
              <strong>{templateName ? `"${templateName}"` : "this template"}</strong>?
            </Text>
          </Modal.Body>

          <Modal.Actions>
            <Button
              variant="secondary"
              onPress={onClose}
              isDisabled={isPublishing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleConfirm}
              isDisabled={isPublishing}
            >
              {isPublishing ? "Publishing..." : "Publish"}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};
